import React from 'react';

// Base skeleton component
export const Skeleton = ({ className = '', ...props }) => {
    return (
        <div 
            className={`animate-pulse bg-gray-200 dark:bg-slate-700 rounded ${className}`}
            {...props}
        />
    );
};

// Card skeleton for TakeOffSiteBox
export const TakeOffSiteBoxSkeleton = () => {
    return (
        <div className="w-full sm:w-1/2 2xl:w-1/3 p-2 sm:p-3">
            <div className="bg-white dark:bg-slate-800 rounded-xl shadow-lg border-2 border-gray-200 dark:border-slate-700 overflow-hidden">
                {/* Background Image Skeleton */}
                <div className="h-40 bg-gray-200 dark:bg-slate-700 animate-pulse relative">
                    {/* Site Name Badge Skeleton */}
                    <div className="absolute top-2 sm:top-3 left-2 sm:left-3">
                        <div className="backdrop-blur-sm rounded-lg px-2 sm:px-3 py-1.5 sm:py-2 shadow-lg bg-white/95 dark:bg-slate-900/95">
                            <div className="flex items-center space-x-1 sm:space-x-2">
                                <Skeleton className="w-3 sm:w-4 h-3 sm:h-4" />
                                <Skeleton className="h-3 sm:h-4 w-20 sm:w-24" />
                            </div>
                        </div>
                    </div>
                </div>

                {/* Weather Data Skeleton */}
                <div className="px-3 sm:px-4 py-2.5 sm:py-3">
                    <div className="flex justify-between items-center">
                        <div className="flex items-center space-x-2 sm:space-x-3">
                            {/* Wind direction skeleton */}
                            <div className="flex flex-col items-center">
                                <Skeleton className="w-3 h-3 rounded-full" />
                                <Skeleton className="h-2 w-4 mt-1" />
                            </div>
                            {/* Wind speed skeleton */}
                            <div className="flex items-center space-x-1">
                                <Skeleton className="w-3 sm:w-4 h-3 sm:h-4" />
                                <Skeleton className="h-4 sm:h-5 w-6 sm:w-8" />
                                <Skeleton className="h-3 sm:h-4 w-6" />
                            </div>
                        </div>
                        {/* Temperature skeleton */}
                        <div className="flex items-center space-x-1">
                            <Skeleton className="w-3 sm:w-4 h-3 sm:h-4" />
                            <Skeleton className="h-4 sm:h-5 w-8 sm:w-10" />
                            <Skeleton className="h-3 sm:h-4 w-4" />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

// Weather box skeleton for dashboard. Body bars are centered on mobile to
// match the loaded WeatherDataBox layout (which centers values via
// `justify-center md:justify-start`); on md+ they revert to left-aligned.
export const WeatherBoxSkeleton = ({ className = "" }) => {
    return (
        <div className={`bg-white dark:bg-slate-800 rounded-xl shadow-lg border border-gray-200 dark:border-slate-700 p-4 ${className}`}>
            <div className="flex items-center justify-between mb-3">
                <Skeleton className="h-5 w-24" />
                <Skeleton className="w-5 h-5 rounded-full" />
            </div>
            <div className="space-y-3">
                <Skeleton className="h-8 w-16 mx-auto md:mx-0" />
                <Skeleton className="h-4 w-20 mx-auto md:mx-0" />
                <div className="flex space-x-2 justify-center md:justify-start">
                    <Skeleton className="h-3 w-12" />
                    <Skeleton className="h-3 w-8" />
                </div>
            </div>
        </div>
    );
};

// Search result skeleton
export const SearchResultSkeleton = () => {
    return (
        <div className="text-xs text-gray-400 dark:text-gray-500 flex items-center space-x-1">
            <Skeleton className="h-3 w-12" />
        </div>
    );
};

// Station dashboard skeleton
export const StationDashboardSkeleton = () => {
    return (
        <div className="bg-zinc-100 dark:bg-slate-900 min-h-screen">
            {/* Header Skeleton */}
            <div className="bg-gray-200 dark:bg-slate-800 pl-3 pt-3 md:pt-0 flex flex-col md:flex-row items-center ring-1 ring-slate-900/5 drop-shadow-lg justify-between">
                <Skeleton className="h-8 sm:h-10 w-20 mr-5 p-2" />
                <div className="flex-1 max-w-md">
                    <Skeleton className="h-6 w-48 mb-2" />
                    <Skeleton className="h-4 w-32" />
                </div>
            </div>

            {/* Content Skeleton */}
            <div className="flex p-2 mt-5 flex-col md:flex-row flex-wrap">
                {/* Map Box Skeleton */}
                <div className="w-full md:w-1/3 lg:w-1/4 my-2 sm:p-2">
                    <div className="rounded-xl ring-1 ring-slate-900/10 shadow-md overflow-hidden">
                        <Skeleton className="h-[480px] w-full rounded-none" />
                    </div>
                </div>

                {/* Weather Boxes Grid Skeleton */}
                <div className="flex p-0 xl:p-2 flex-wrap flex-1">
                    {[...Array(7)].map((_, i) => (
                        <div key={i} className="w-full md:w-1/2 lg:w-1/3 my-2">
                            <div className="bg-white dark:bg-slate-800 rounded-lg py-3 px-3 m-0 sm:m-2 ring-1 ring-slate-900/5 shadow-xl">
                                <div className="flex items-center justify-between mb-3">
                                    <Skeleton className="h-5 w-24" />
                                    <Skeleton className="w-5 h-5 rounded-full" />
                                </div>
                                <div className="my-3 space-y-3">
                                    <Skeleton className="h-8 w-16 mx-auto md:mx-0" />
                                    <Skeleton className="h-4 w-20 mx-auto md:mx-0" />
                                    <div className="flex space-x-2 justify-center md:justify-start">
                                        <Skeleton className="h-3 w-12" />
                                        <Skeleton className="h-3 w-8" />
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Charts Section Skeleton */}
            <div className="flex flex-wrap relative p-2">
                <div className="w-full xl:w-1/3 my-2">
                    <div className="bg-white dark:bg-slate-800 rounded-lg py-3 px-3 m-0 sm:m-2 ring-1 ring-slate-900/5 shadow-xl">
                        <div className="flex justify-between items-center mb-3">
                            <Skeleton className="h-5 w-32" />
                        </div>
                        <div className="my-3">
                            <Skeleton className="h-64 w-full rounded" />
                        </div>
                    </div>
                </div>
                <div className="w-full xl:w-2/3 my-2">
                    <div className="bg-white dark:bg-slate-800 rounded-lg py-3 px-3 m-0 sm:m-2 ring-1 ring-slate-900/5 shadow-xl">
                        <div className="flex justify-between items-center mb-3">
                            <Skeleton className="h-5 w-40" />
                        </div>
                        <div className="my-3">
                            <Skeleton className="h-64 w-full rounded" />
                        </div>
                    </div>
                </div>
                {['h-72', 'h-64'].map((h, i) => (
                    <div key={i} className="w-full my-2">
                        <div className="bg-white dark:bg-slate-800 rounded-lg py-3 px-3 m-0 sm:m-2 ring-1 ring-slate-900/5 shadow-xl">
                            <div className="flex justify-between items-center mb-3"><Skeleton className="h-5 w-48" /></div>
                            <div className="my-3"><Skeleton className={`${h} w-full rounded`} /></div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default Skeleton;